import { Link, useLocation } from 'react-router-dom';
import { useCompanyBranding, useCompanyContact, useCompanySocial } from '../../hooks/unified';
import type { FooterProps } from '../../types/navigation';

export function Footer({ companyInfo, socialLinks, contactInfo }: FooterProps) {
  const location = useLocation();
  const isDashboardPage = location.pathname.startsWith('/dashboard');
  const currentYear = new Date().getFullYear();

  // Get dynamic data from system settings
  const branding = useCompanyBranding();
  const contact = useCompanyContact();
  const social = useCompanySocial();

  // Default logo from public folder
  const defaultLogo = '/logo192.png';
  
  const effectiveCompanyInfo = {
    name: branding.name || companyInfo.name,
    tagline: branding.tagline || companyInfo.tagline,
    logo: branding.logo || companyInfo.logo || defaultLogo,
  };

  const effectiveContactInfo = {
    address: contact.address || contactInfo.address,
    phone: contact.phone || contactInfo.phone,
    email: contact.email || contactInfo.email,
  };

  const effectiveSocialLinks = [
    ...(social.linkedin ? [{ platform: 'linkedin', url: social.linkedin }] : []),
    ...(social.x ? [{ platform: 'twitter', url: social.x }] : []),
    ...(social.facebook ? [{ platform: 'facebook', url: social.facebook }] : []),
    ...(social.instagram ? [{ platform: 'instagram', url: social.instagram }] : []),
  ].length > 0
    ? [
        ...(social.linkedin ? [{ platform: 'linkedin', url: social.linkedin }] : []),
        ...(social.x ? [{ platform: 'twitter', url: social.x }] : []),
        ...(social.facebook ? [{ platform: 'facebook', url: social.facebook }] : []),
        ...(social.instagram ? [{ platform: 'instagram', url: social.instagram }] : []),
      ]
    : socialLinks;

  const quickLinks = [
    { to: '/jobs', label: 'Browse Jobs' },
    { to: '/about', label: 'About Us' },
    { to: '/services', label: 'Services' },
    { to: '/contact', label: 'Contact' },
  ];

  const legalLinks = [
    { to: '/privacy', label: 'Privacy Policy' },
    { to: '/terms', label: 'Terms of Service' },
  ];

  const SocialIcon = ({ platform }: { platform: string }) => {
    const icons: Record<string, React.ReactNode> = {
      linkedin: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
      twitter: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      facebook: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      instagram: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
      ),
    };
    return icons[platform] || null;
  };

  return (
    <footer className={`bg-gradient-to-b from-gray-900 to-gray-950 ${isDashboardPage ? 'lg:ml-64' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Brand Section */}
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                {effectiveCompanyInfo.logo ? (
                  <img
                    src={effectiveCompanyInfo.logo}
                    alt={`${effectiveCompanyInfo.name} logo`}
                    className="h-9 w-auto max-w-[180px] object-contain"
                  />
                ) : (
                  <div className="h-10 w-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
                    <span className="text-white font-bold text-xl">
                      {effectiveCompanyInfo.name.charAt(0)}
                    </span>
                  </div>
                )}
                <span className="text-xl font-semibold text-white">{effectiveCompanyInfo.name}</span>
              </div>
              {effectiveCompanyInfo.tagline && (
                <p className="text-gray-300 text-base mb-4">{effectiveCompanyInfo.tagline}</p>
              )}
              <p className="text-gray-400 text-sm leading-relaxed max-w-md">
                {branding.description ||
                  'Connecting talented professionals with exceptional opportunities. Building successful careers and thriving businesses together.'}
              </p>

              {/* Social Links */}
              {effectiveSocialLinks.length > 0 && (
                <div className="flex items-center gap-3 mt-6">
                  {effectiveSocialLinks.map((link) => (
                    <a
                      key={link.platform}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-lg bg-gray-800/50 hover:bg-primary-600 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200"
                      aria-label={`Follow us on ${link.platform}`}
                    >
                      <SocialIcon platform={link.platform} />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Links Sections */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
                {/* Quick Links */}
                <div>
                  <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
                    Explore
                  </h4>
                  <ul className="space-y-3">
                    {quickLinks.map((link) => (
                      <li key={link.to}>
                        <Link
                          to={link.to}
                          className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Legal Links */}
                <div>
                  <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
                    Legal
                  </h4>
                  <ul className="space-y-3">
                    {legalLinks.map((link) => (
                      <li key={link.to}>
                        <Link
                          to={link.to}
                          className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Contact */}
                <div>
                  <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
                    Contact
                  </h4>
                  <ul className="space-y-3">
                    {effectiveContactInfo.email && (
                      <li>
                        <a
                          href={`mailto:${effectiveContactInfo.email}`}
                          className="text-gray-400 hover:text-white text-sm transition-colors duration-200 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="truncate">{effectiveContactInfo.email}</span>
                        </a>
                      </li>
                    )}
                    {effectiveContactInfo.phone && (
                      <li>
                        <a
                          href={`tel:${effectiveContactInfo.phone}`}
                          className="text-gray-400 hover:text-white text-sm transition-colors duration-200 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span>{effectiveContactInfo.phone}</span>
                        </a>
                      </li>
                    )}
                    {effectiveContactInfo.address && (
                      <li className="text-gray-400 text-sm flex items-start gap-2">
                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{effectiveContactInfo.address}</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800/50 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} {effectiveCompanyInfo.name}. All rights reserved.
            </p>
            <p className="text-gray-600 text-xs">
              Connecting talent with opportunity
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
